import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HexagonIcon } from "@phosphor-icons/react";
// INV-2704: reader-added widgets are hidden until their styling is tested on the twin's own widgets.
// import { toast } from "sonner";
import { RqaSimLauncher } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/RqaSimIsland/blocks/RqaSim";
import { SidePanel } from "@libs/zapi-ui/ds/components/side-panel";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { WidgetCardBody } from "@libs/zapi-ui/ds/components/widget-card";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { useRqaSimConfigureStore } from "@/zapi-frontend/stores/rqaSimConfigure";
import { applyRefs } from "@/zapi-frontend/shared/lib/rqaSim/twin";
import type { TTwinHeadlineBox } from "@/zapi-frontend/shared/types/rqaSim";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { renderBlock } from "../MemoIsland/ChapterBlocks";
import { FigureRefsProvider } from "../MemoIsland/FigureRefs/FigureRefsProvider";
import { ContextEmpty } from "../ProjectLayout/ui/ContextEmpty";
import { WidgetCard, WidgetCardHeader, WidgetTiles } from "../ProjectLayout/ui/WidgetCard";
import { WORKSPACE_ASIDE_SLOT_ID } from "../ProjectLayout/lib/layoutSlots";
import { useRqaSim } from "./useRqaSim";
// import { useRqaSimWidgetActions } from "./useRqaSim";
import { TwinBox } from "./_TwinBox";
import { TwinConfigure } from "./_TwinConfigure";
// import { TwinPin } from "./_TwinPin";
// import { TwinPinnedBand } from "./_TwinPinnedBand";
import { RQASIM } from "./_tokens";

const TONES = ["var(--innovera-zinc900)", "var(--innovera-zinc500)"];
const toneOf = (index: number) => TONES[index] ?? TONES[0];

export function RqaSimIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { isChatDocked } = useWorkspaceLayout();
  const isDocked = useMediaQuery("(min-width: 1332px)");
  const { id } = useParams();
  const projectId = typeof id === "string" ? id : "";
  const { role, accessMode } = useMeStore();
  const readOnly = !hasEditAccess(role, accessMode);
  const sim = useRqaSim(projectId);
  // INV-2704
  // const widgetActions = useRqaSimWidgetActions(projectId, (message) => toast.error(message));
  const configureOpen = useRqaSimConfigureStore((store) => store.isOpen);
  const configureClosing = useRqaSimConfigureStore((store) => store.isClosing);
  const setConfigureOpen = useRqaSimConfigureStore((store) => store.setOpen);
  const dismissConfigure = useRqaSimConfigureStore((store) => store.dismiss);
  useEffect(() => () => dismissConfigure(), [dismissConfigure]);
  const { twin, values } = sim;

  if (sim.isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center" data-testid="rqa-sim-loading">
        <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />
      </div>
    );
  }

  const header = (
    <div className="flex min-h-10 shrink-0 items-center" data-testid="rqa-sim-header">
      <div className="mx-auto flex w-full max-w-[960px] flex-wrap items-center justify-between gap-3">
        <h1 className="typo-h3 shrink-0 text-foreground">{RQASIM}</h1>
        {/* INV-2704
        {twin && (
          <TwinPin
            options={sim.pinnable}
            pending={widgetActions.add.isPending || widgetActions.describe.isPending}
            full={sim.pinsLeft <= 0}
            onPick={(option) => widgetActions.add.mutate({ key: option.key })}
            onDescribe={(prompt) => widgetActions.describe.mutate({ prompt })}
          />
        )} */}
      </div>
    </div>
  );

  if (!twin) {
    return (
      <div className="flex h-full w-full flex-col" data-testid="rqa-sim-empty">
        {header}
        <div className="mx-auto w-full max-w-[960px] px-4 pt-2">
          <ContextEmpty
            icon={HexagonIcon}
            description={sim.hasLedger ? t("rqaSim.empty") : t("rqaSim.emptyNoLedger")}
          />
        </div>
      </div>
    );
  }

  const panels = twin.panels.filter((panel) => (panel.headline ?? []).length > 0 || panel.note);
  const blocks = applyRefs(twin.blocks, sim.twinValues);
  const held = sim.unresolved.filter((key) => !sim.noReturn.includes(key));
  const listKeys = (keys: string[]) => keys.slice(0, 3).map(sim.labelOf).join(", ") + (keys.length > 3 ? ", …" : "");
  const wasOf = (key: string) => {
    const base = sim.baseValues.get(key);
    return base !== undefined && values.has(key) && values.get(key) !== base ? sim.spell(key, base) : undefined;
  };

  const tilesOf = (boxes: TTwinHeadlineBox[]) => (
    <WidgetTiles>
      {boxes.map((box, index) => (
        <TwinBox
          key={`${box.key}-${index}`}
          size="band"
          figureKey={box.key}
          label={box.label ?? sim.labelOf(box.key)}
          value={sim.spellParts(box.key, values.get(box.key))}
          stale={sim.unresolved.includes(box.key)}
          noReturn={sim.noReturn.includes(box.key)}
          was={wasOf(box.key)}
        />
      ))}
    </WidgetTiles>
  );

  const configureShown = configureOpen && !configureClosing;
  const launcherProps = {
    onClick: () => setConfigureOpen(true),
    "aria-label": t("rqaSim.open"),
    "aria-expanded": false,
    "data-testid": "rqa-sim-configure-open"
  };
  const asideSlot = configureOpen ? document.getElementById(WORKSPACE_ASIDE_SLOT_ID) : null;
  const docked = isDocked && !!asideSlot;
  const twinConfigure = configureOpen && (
    <TwinConfigure
      twin={twin}
      sim={sim}
      readOnly={readOnly}
      closing={configureClosing}
      docked={docked}
      spell={sim.spell}
      onClose={() => setConfigureOpen(false)}
    />
  );
  const configure =
    twinConfigure &&
    (docked && asideSlot
      ? createPortal(
          <SidePanel
            open={configureShown}
            className="sticky top-(--app-header-height) h-[calc(100svh-var(--app-header-height))] shrink-0"
            contentClassName="h-full w-[26rem] py-4"
          >
            {twinConfigure}
          </SidePanel>,
          asideSlot
        )
      : createPortal(twinConfigure, asideSlot ?? document.body));

  return (
    <div className="relative flex w-full flex-col" data-testid="rqa-sim-island">
      {header}
      <FigureRefsProvider figures={sim.figuresAtSettings}>
        <div className="@container mx-auto flex w-full max-w-[960px] flex-col gap-4 pb-28 pt-6">
          {(twin.intent || twin.headline.length > 0) && (
            <WidgetCard data-testid="rqa-sim-overview">
              {twin.title && <WidgetCardHeader title={twin.title} titleOverflow="wrap" />}
              <WidgetCardBody className="min-w-0">
                {twin.intent && <p className="typo-body-md max-w-[68ch] text-greyscale-800">{twin.intent}</p>}
                {twin.headline.length > 0 && tilesOf(twin.headline)}
              </WidgetCardBody>
            </WidgetCard>
          )}

          {/* INV-2704
          <TwinPinnedBand
            widgets={sim.widgets.filter((widget) => !widgetActions.hidden.includes(widget.id))}
            valueOf={(key) => sim.spellParts(key, values.get(key))}
            labelOf={sim.labelOf}
            twinValues={sim.twinValues}
            isHeld={(key) => sim.unresolved.includes(key)}
            hasNoReturn={(key) => sim.noReturn.includes(key)}
            wasOf={wasOf}
            onUnpin={(widget) =>
              widgetActions.unpin(widget.id, {
                removed: t("rqaSim.removedTile", {
                  name: widget.key ? sim.labelOf(widget.key) : widget.prompt ?? ""
                }),
                undo: t("rqaSim.undo")
              })
            }
          /> */}

          {panels.length > 0 && (
            <div className="grid gap-4 @2xl:grid-cols-2" data-testid="rqa-sim-panels">
              {panels.map((panel, index) => (
                <WidgetCard key={panel.id} data-testid={`rqa-sim-panel-${panel.id}`}>
                  <WidgetCardHeader
                    title={panel.label}
                    titleIcon={<span className="size-2 shrink-0 rounded-full" style={{ background: toneOf(index) }} />}
                  />
                  <WidgetCardBody className="min-w-0 gap-4">
                    {panel.note && <p className="typo-body-sm text-muted-foreground">{panel.note}</p>}
                    {(panel.headline ?? []).length > 0 && tilesOf(panel.headline ?? [])}
                  </WidgetCardBody>
                </WidgetCard>
              ))}
            </div>
          )}

          {(held.length > 0 || sim.noReturn.length > 0) && (
            <section className="flex flex-col gap-1 px-1" data-testid="rqa-sim-notes">
              {held.length > 0 && (
                <p className="typo-caption max-w-[68ch] text-innoveraRed-700">
                  {t(held.length === 1 ? "rqaSim.unresolvedOne" : "rqaSim.unresolvedMany", {
                    count: held.length,
                    keys: listKeys(held)
                  })}
                </p>
              )}
              {sim.noReturn.length > 0 && (
                <p className="typo-caption max-w-[68ch] text-muted-foreground">
                  {t(sim.noReturn.length === 1 ? "rqaSim.noReturnOne" : "rqaSim.noReturnMany", {
                    count: sim.noReturn.length,
                    keys: listKeys(sim.noReturn)
                  })}
                </p>
              )}
            </section>
          )}

          {blocks.length > 0 && (
            <WidgetCard data-testid="rqa-sim-body">
              <WidgetCardHeader title={t("rqaSim.body")} />
              <WidgetCardBody className="min-w-0 gap-4 [&_figure.border]:bg-card">
                {blocks.map((block, index) => renderBlock(block, index))}
              </WidgetCardBody>
            </WidgetCard>
          )}
        </div>
      </FigureRefsProvider>

      {/* While the docked Leo is hidden, its launcher takes the corner, so this one sits beside it as on small screens. */}
      {!configureShown && (
        <>
          {isChatDocked && (
            <div className="pointer-events-none sticky bottom-5 z-20 mt-auto hidden justify-end pt-6 lg:flex lg:[body:has([data-docked-closed])_&]:hidden">
              <RqaSimLauncher className="pointer-events-auto" {...launcherProps} />
            </div>
          )}
          <div
            className={cn(
              "fixed end-21 bottom-5 z-10",
              isChatDocked && "lg:hidden lg:[body:has([data-docked-closed])_&]:block"
            )}
          >
            <RqaSimLauncher {...launcherProps} />
          </div>
        </>
      )}
      {configure}
    </div>
  );
}
